import { Deferred } from "./deferred"

type Timeout = ReturnType<typeof globalThis.setTimeout>

export class Lock {
	private internalLock: Deferred<void>
	private timeout: number

	private internalLockTimeout: Timeout

	public constructor(timeout: number = 30000) {
		this.timeout = timeout

		this.internalLock = new Deferred<void>()
		this.internalLock.resolve()

		this.internalLockTimeout = globalThis.setTimeout(() => void 0)
	}

	public async lock() {
		await this.internalLock.promise

		this.internalLock = new Deferred<void>()

		this.internalLockTimeout = globalThis.setTimeout(() => {
			this.internalLock.resolve()
		}, this.timeout)
	}

	public unlock() {
		globalThis.clearTimeout(this.internalLockTimeout)

		this.internalLock.resolve()
	}
}

export class AsyncQueueLock {
	private locked = false
	private queue: Array<() => void> = []
	private timeout: number
	private currentTimer?: Timeout

	public get isLocked() {
		return this.locked
	}

	public get count() {
		return this.queue.length
	}

	constructor(timeout = 30000) {
		this.timeout = timeout
	}

	async lock(): Promise<void> {
		if (!this.locked) {
			this.locked = true
			this.startTimeout()
			return
		}

		return new Promise<void>((resolve) => {
			this.queue.push(resolve)
		})
	}

	unlock(): void {
		if (!this.locked) return

		this.clearCurrentTimer()
		const next = this.queue.shift()
		if (!next) {
			this.locked = false
		} else {
			// The next waiting promise acquires the lock now
			next()
			this.startTimeout()
		}
	}

	private startTimeout(): void {
		this.clearCurrentTimer()
		if (this.timeout > 0) {
			this.currentTimer = globalThis.setTimeout(() => {
				if (this.locked) {
					this.unlock()
				}
			}, this.timeout)
		}
	}

	private clearCurrentTimer(): void {
		if (this.currentTimer !== undefined) {
			clearTimeout(this.currentTimer)
			this.currentTimer = undefined
		}
	}
}
